/**
 * The Readings a Custom Alert can judge, per Asset Class.
 *
 * `metric` is the `reading` column the pipeline compares (ProcessorAlertRules), so it
 * must match the back end's CUSTOM_ALERT_FIELDS in
 * core/logiq/api/experience/experience/gql/alert_templates_schema.py, which validates it.
 * Shared: the asset editor offers these fields, and every alert card names them.
 * A field listed here that an Asset doesn't report never fires: accepted (PLAN.md D5).
 */

export interface CustomAlertField {
  metric: string
  label: string
  unit: string
}

const FIELDS: Record<string, CustomAlertField> = Object.fromEntries(
  (
    [
      ["power", "Power", "W"],
      ["power_1", "Power L1", "W"],
      ["power_2", "Power L2", "W"],
      ["power_3", "Power L3", "W"],
      ["upstream_power", "Upstream Power", "W"],
      ["state_of_charge", "State of Charge", "%"],
      ["voltage", "Voltage", "V"],
      ["voltage_1", "Voltage L1", "V"],
      ["voltage_2", "Voltage L2", "V"],
      ["voltage_3", "Voltage L3", "V"],
      ["current", "Current", "A"],
      ["current_1", "Current L1", "A"],
      ["current_2", "Current L2", "A"],
      ["current_3", "Current L3", "A"],
      ["level", "Level", "%"],
      ["weight", "Weight", "kg"],
      ["pressure", "Pressure", "bar"],
      ["pressure_manifold", "Manifold Pressure", "bar"],
      ["temperature", "Temperature", "°C"],
    ] as const
  ).map(([metric, label, unit]) => [metric, { metric, label, unit }]),
)

const THREE_PHASE_POWER = ["power", "power_1", "power_2", "power_3"]

const BY_CLASS: Record<string, string[]> = {
  battery: ["state_of_charge", "power", "voltage", "current", "temperature"],
  genset: [...THREE_PHASE_POWER, "voltage", "current", "temperature"],
  inverter_charger: [...THREE_PHASE_POWER, "upstream_power", "voltage", "current"],
  solar: ["power", "voltage", "current"],
  power: [...THREE_PHASE_POWER, "voltage", "current"],
  distro: [...THREE_PHASE_POWER, "voltage", "current"],
  electrolyser: ["power", "pressure", "temperature"],
  fuel: ["level", "weight", "temperature"],
  storage: ["level", "weight", "pressure", "pressure_manifold", "temperature"],
  gas: ["pressure", "pressure_manifold", "weight", "temperature"],
  refueller: ["pressure", "weight", "temperature"],
}

const DEFAULT_FIELDS = ["power", "voltage", "current", "temperature"]

export function fieldsForAssetClass(assetClass?: string | null): CustomAlertField[] {
  const metrics = BY_CLASS[(assetClass ?? "").toLowerCase()] ?? DEFAULT_FIELDS
  return metrics.map((metric) => FIELDS[metric])
}

export function customAlertField(metric?: string | null): CustomAlertField | undefined {
  return metric ? FIELDS[metric] : undefined
}

/** The six `comparison_operator` values, named in words as in the design, with their symbols. */
export const OPERATORS: Array<{ value: string; label: string; symbol: string }> = [
  { value: "gt", label: "above", symbol: ">" },
  { value: "gte", label: "at or above", symbol: "≥" },
  { value: "lt", label: "below", symbol: "<" },
  { value: "lte", label: "at or below", symbol: "≤" },
  { value: "eq", label: "equal to", symbol: "=" },
  { value: "neq", label: "not equal to", symbol: "≠" },
]

/** The operator in words, for use mid-sentence: "Power above 5000 W". */
export function operatorLabel(operator?: string | null): string {
  return OPERATORS.find((o) => o.value === operator?.toLowerCase())?.label ?? ""
}

/** The operator's words and symbol together, for a comparison on its own (a column or dropdown). */
export function operatorWithSymbol(operator?: string | null): string {
  const found = OPERATORS.find((o) => o.value === operator?.toLowerCase())
  return found ? `${found.label} (${found.symbol}) ` : ""
}
